import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Form, Button, Select, message, Typography, Spin, Card, Radio } from "antd";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";
import warehouseApi from "../api/warehouseApi";
import "./WarehouseSettingsPage.scss";

const { Text, Title } = Typography;

const WarehouseSettingsPage = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const returnTo = queryParams.get("returnTo") || "/warehouses";

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setInitialLoading(true);
    try {
      const settings = await warehouseApi.getSettings();
      form.setFieldsValue({
        itemCategories: settings.itemCategories || [],
        measurementUnit: settings.measurementUnit || "in"
      });
    } catch (error) {
      console.error("Error loading settings:", error);
      message.error("Failed to load settings");
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      await warehouseApi.updateSettings({
        itemCategories: values.itemCategories || [],
        measurementUnit: values.measurementUnit || "in"
      });

      message.success("Settings updated successfully");
      history.push(returnTo);
    } catch (error) {
      console.error("Error updating settings:", error);
      message.error("Failed to update settings");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    history.push(returnTo);
  };

  if (initialLoading) {
    return (
      <div className="warehouse-settings-page loading-container">
        <Spin size="large" />
        <Text>Loading settings...</Text>
      </div>
    );
  }

  return (
    <div className="warehouse-settings-page">
      <div className="page-header">
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={handleCancel}
          className="back-button"
        >
          {returnTo === "/warehouses" ? "Back to Warehouses" : "Back to Inventory"}
        </Button>
        <Title level={2}>Warehouse Settings</Title>
      </div>

      <Card className="settings-card">
        <Title level={4}>Item Attributes</Title>
        <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
          Configure global item attributes that will be available when creating or editing
          inventory items across all warehouses.
        </Text>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="settings-form"
        >
          <Form.Item
            name="itemCategories"
            label="Item Attributes"
            extra="Type attribute name and press Enter to add. Attributes will be available in dropdown when managing items."
          >
            <Select
              mode="tags"
              style={{ width: "100%" }}
              placeholder="Type and press Enter to add attribute"
              tokenSeparators={[","]}
            />
          </Form.Item>

          <Form.Item
            name="measurementUnit"
            label="Measurement Unit"
            extra="Choose the unit of measurement for displaying package dimensions."
          >
            <Radio.Group buttonStyle="solid">
              <Radio.Button value="in">Inches (in)</Radio.Button>
              <Radio.Button value="cm">Centimeters (cm)</Radio.Button>
            </Radio.Group>
          </Form.Item>

          <div className="form-footer">
            <Button
              onClick={handleCancel}
              className="cancel-button"
              style={{ color: "white" }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<SaveOutlined />}
              className="save-button"
            >
              {loading ? "Saving..." : "Save Settings"}
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default WarehouseSettingsPage;
